import type {
	CustomerQuoteBoardItem,
	QuoteBoardItem,
} from "~/shared/schemas/quote-board-schema.ts";

import { QuoteBoardCard } from "./quote-board-card.tsx";
import { QuoteBoardCardBatchHeader } from "./quote-board-card-batch-header.tsx";
import styles from "./quote-board-card-list.module.css";

type BoardItem = QuoteBoardItem | CustomerQuoteBoardItem;

type QuoteBoardCardGroup = {
	key: string | number;
	bulkQuoteId: string | null;
	items: BoardItem[];
	sortDate: number;
};

const getGroupSortDate = (items: BoardItem[]): number => {
	const timestamps: number[] = [];
	for (const item of items) {
		const value = item.bulkReceivedAtUtc ?? item.createdDate;
		if (!value) continue;
		const timestamp = new Date(value).getTime();
		if (!Number.isNaN(timestamp)) timestamps.push(timestamp);
	}
	return timestamps.length > 0
		? Math.max(...timestamps)
		: Number.NEGATIVE_INFINITY;
};

/**
 * Groups items by batch (matching the grid's `displayGroupingId`: bulkQuoteId
 * when present, otherwise the quote stands alone), newest batch first.
 */
const groupQuoteBoardCardItems = (
	items: BoardItem[],
): QuoteBoardCardGroup[] => {
	const groupMap = new Map<string | number, BoardItem[]>();
	for (const item of items) {
		const key = item.bulkQuoteId ?? item.quoteId;
		const group = groupMap.get(key);
		if (group) {
			group.push(item);
		} else {
			groupMap.set(key, [item]);
		}
	}

	return [...groupMap.entries()]
		.map(([key, groupItems]) => ({
			key,
			bulkQuoteId: groupItems[0]?.bulkQuoteId ?? null,
			items: groupItems,
			sortDate: getGroupSortDate(groupItems),
		}))
		.sort((left, right) => right.sortDate - left.sortDate);
};

type QuoteBoardCardListProps = {
	items: BoardItem[];
	isLoading?: boolean;
	isInternal?: boolean;
	selectedQuoteIds?: ReadonlySet<number>;
	showBulkTenderSelection?: boolean;
	onToggleQuoteSelection?: (quoteId: number, selected: boolean) => void;
	onToggleBatchSelection?: (
		bulkQuoteId: string,
		selected: boolean,
		visibleQuoteIds: readonly number[],
	) => void;
	onOpenCarrierSelection?: (quoteId: number) => void;
	onOpenBulkCarrierSelection?: (quoteId: number) => void;
};

export const QuoteBoardCardList = ({
	items,
	isLoading = false,
	isInternal = false,
	selectedQuoteIds,
	showBulkTenderSelection = false,
	onToggleQuoteSelection,
	onToggleBatchSelection,
	onOpenCarrierSelection,
	onOpenBulkCarrierSelection,
}: QuoteBoardCardListProps) => {
	if (isLoading) {
		return (
			<div
				className={styles.status}
				data-testid={"quote-board-card-list-loading"}
			>
				Loading quotes...
			</div>
		);
	}

	if (items.length === 0) {
		return (
			<div
				className={styles.status}
				data-testid={"quote-board-card-list-empty"}
			>
				No quotes found.
			</div>
		);
	}

	const groups = groupQuoteBoardCardItems(items);

	return (
		<div className={styles.list} data-testid={"quote-board-card-list"}>
			{groups.map((group) => (
				<div key={group.key} className={styles.group}>
					{group.bulkQuoteId ? (
						<QuoteBoardCardBatchHeader
							bulkQuoteId={group.bulkQuoteId}
							isInternal={isInternal}
							items={group.items}
							onToggleBatchSelection={onToggleBatchSelection}
							selectedQuoteIds={selectedQuoteIds}
							showBulkTenderSelection={showBulkTenderSelection}
						/>
					) : null}
					{group.items.map((item) => (
						<QuoteBoardCard
							key={item.quoteId}
							isInternal={isInternal}
							isSelected={selectedQuoteIds?.has(item.quoteId) ?? false}
							item={item}
							onOpenBulkCarrierSelection={onOpenBulkCarrierSelection}
							onOpenCarrierSelection={onOpenCarrierSelection}
							onToggleSelection={onToggleQuoteSelection}
							showBulkTenderSelection={showBulkTenderSelection}
						/>
					))}
				</div>
			))}
		</div>
	);
};
